import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { XMarkIcon, PencilIcon, CloudArrowUpIcon, XCircleIcon } from '@heroicons/react/24/outline';
import AlertDialog from './AlertDialog';

const BannerModal = ({ isOpen = true, banner, onClose, onSave, defaultPageType = 'home' }) => {
    const [formData, setFormData] = useState({
        title: '',
        subtitle: '',
        primary_button_text: '',
        primary_button_link: '',
        secondary_button_text: '',
        secondary_button_link: '',
        page_type: defaultPageType,
        display_order: 0,
        is_active: true,
        highlights: []
    });

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [dimensionAlert, setDimensionAlert] = useState({ isOpen: false, width: 0, height: 0, file: null });
    const [highlightInput, setHighlightInput] = useState('');
    const [loading, setLoading] = useState(false);

    // Initialize form data when editing
    useEffect(() => {
        if (banner) {
            setFormData({
                title: banner.title || '',
                subtitle: banner.subtitle || '',
                primary_button_text: banner.primary_button_text || '',
                primary_button_link: banner.primary_button_link || '',
                secondary_button_text: banner.secondary_button_text || '',
                secondary_button_link: banner.secondary_button_link || '',
                page_type: banner.page_type || 'home',
                display_order: banner.display_order || 0,
                is_active: banner.is_active !== undefined ? banner.is_active : true,
                highlights: (() => {
                    try {
                        const parsed = typeof banner.highlights === 'string'
                            ? JSON.parse(banner.highlights)
                            : banner.highlights;
                        return Array.isArray(parsed) ? parsed : [];
                    } catch (e) {
                        console.error('Error parsing highlights:', e);
                        return [];
                    }
                })()
            });

            if (banner.image_url && typeof banner.image_url === 'string') {
                try {
                    const imageUrl = banner.image_url.startsWith('http')
                        ? banner.image_url
                        : `/${banner.image_url.startsWith('/') ? banner.image_url.slice(1) : banner.image_url}`;
                    const timestamp = banner.updated_at ? new Date(banner.updated_at).getTime() : Date.now();
                    setImagePreview(`${imageUrl}?t=${timestamp}`);
                } catch (err) {
                    console.error('Error setting image preview:', err);
                }
            }
        }
    }, [banner, defaultPageType]);

    // Handle input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle image selection
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.match(/^image\/(webp|png)$/)) {
                toast.error('Only WebP and PNG formats are allowed');
                e.target.value = '';
                return;
            }

            // Validate dimensions
            const img = new Image();
            const reader = new FileReader();

            reader.onloadend = () => {
                img.src = reader.result;
                img.onload = () => {
                    const width = img.width;
                    const height = img.height;

                    // Recommended dimensions: 1200 x 500
                    if (width !== 1600 || height !== 800) {
                        setDimensionAlert({
                            isOpen: true,
                            width,
                            height,
                            file
                        });
                        e.target.value = '';
                        return;
                    }

                    setImageFile(file);
                    setImagePreview(reader.result);
                };
            };
            reader.readAsDataURL(file);
        }
    };

    // Add highlight
    const addHighlight = () => {
        if (highlightInput.trim()) {
            setFormData(prev => ({
                ...prev,
                highlights: [...prev.highlights, highlightInput.trim()]
            }));
            setHighlightInput('');
        }
    };

    // Remove highlight
    const removeHighlight = (index) => {
        setFormData(prev => ({
            ...prev,
            highlights: prev.highlights.filter((_, i) => i !== index)
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.title.trim()) {
            toast.error('Title is required');
            return;
        }
        if (!formData.subtitle.trim()) {
            toast.error('Subtitle is required');
            return;
        }

        setLoading(true);

        try {
            const submitData = new FormData();
            submitData.append('title', formData.title);
            submitData.append('subtitle', formData.subtitle);
            submitData.append('primary_button_text', formData.primary_button_text);
            submitData.append('primary_button_link', formData.primary_button_link);
            submitData.append('secondary_button_text', formData.secondary_button_text);
            submitData.append('secondary_button_link', formData.secondary_button_link);
            submitData.append('page_type', formData.page_type);
            submitData.append('display_order', formData.display_order);
            submitData.append('is_active', formData.is_active ? '1' : '0');
            submitData.append('highlights', JSON.stringify(formData.highlights));

            if (imageFile) {
                submitData.append('image', imageFile);
            }

            if (banner) {
                // Update existing banner
                await axios.put(`/api/admin/banners/${banner.id}`, submitData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Banner updated successfully');
            } else {
                // Create new banner
                await axios.post('/api/admin/banners', submitData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Banner created successfully');
            }

            if (onSave) onSave();
        } catch (error) {
            console.error('Error saving banner:', error);
            toast.error(error.response?.data?.message || 'Failed to save banner');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] overflow-y-auto">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>

            {/* Modal */}
            <div className="relative min-h-screen flex items-center justify-center p-4">
                <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                    {/* Header */}
                    <div className="sticky top-0 bg-primary-600 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
                        <div className="flex items-center gap-3">
                            <PencilIcon className="h-6 w-6" />
                            <div>
                                <h2 className="text-xl font-bold">
                                    {banner ? 'Edit Banner' : 'Add New Banner'}
                                </h2>
                                <p className="text-primary-100 text-sm">Update banner details</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-primary-700 rounded-lg transition-colors"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-6 space-y-6">
                        {/* Title */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Title <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                placeholder="Enter banner title"
                            />
                        </div>

                        {/* Subtitle */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Subtitle <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                name="subtitle"
                                value={formData.subtitle}
                                onChange={handleChange}
                                required
                                rows={3}
                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                                placeholder="Enter banner subtitle"
                            />
                        </div>

                        {/* Banner Image */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Banner Image <span className="text-slate-400">(Optional)</span>
                            </label>
                            <p className="text-xs text-slate-500 mb-3">
                                📐 Recommended: 1600 x 800 pixels | Formats: WebP, PNG only
                            </p>

                            <div className="flex gap-4">
                                {/* Upload Area */}
                                <div className="flex-1">
                                    <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-primary-500 transition-colors">
                                        <input
                                            type="file"
                                            accept=".webp,.png,image/webp,image/png"
                                            onChange={handleImageChange}
                                            className="hidden"
                                        />
                                        <CloudArrowUpIcon className="h-12 w-12 text-slate-400 mx-auto mb-2" />
                                        <p className="text-sm font-medium text-slate-600">Change Image</p>
                                        <p className="text-xs text-slate-400 mt-1">WebP or PNG, max 5MB</p>
                                    </label>
                                </div>

                                {/* Image Preview */}
                                {imagePreview && (
                                    <div className="w-48 h-32 rounded-xl overflow-hidden border-2 border-slate-200">
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Button Fields */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Primary Button Text
                                </label>
                                <input
                                    type="text"
                                    name="primary_button_text"
                                    value={formData.primary_button_text}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                    placeholder="e.g., Apply Now"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Primary Button Link
                                </label>
                                <input
                                    type="text"
                                    name="primary_button_link"
                                    value={formData.primary_button_link}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                    placeholder="e.g., /contact"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Secondary Button Text
                                </label>
                                <input
                                    type="text"
                                    name="secondary_button_text"
                                    value={formData.secondary_button_text}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                    placeholder="e.g., EMI Calculator"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Secondary Button Link
                                </label>
                                <input
                                    type="text"
                                    name="secondary_button_link"
                                    value={formData.secondary_button_link}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                    placeholder="e.g., #emi-calculator"
                                />
                            </div>
                        </div>

                        {/* Highlights */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Highlights
                            </label>
                            <div className="flex gap-2 mb-3">
                                <input
                                    type="text"
                                    value={highlightInput}
                                    onChange={(e) => setHighlightInput(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHighlight())}
                                    className="flex-1 px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                    placeholder="Add a highlight"
                                />
                                <button
                                    type="button"
                                    onClick={addHighlight}
                                    className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all"
                                >
                                    Add
                                </button>
                            </div>

                            {/* Highlight Tags */}
                            <div className="flex flex-wrap gap-2">
                                {formData.highlights.map((highlight, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg text-sm font-medium"
                                    >
                                        {highlight}
                                        <button
                                            type="button"
                                            onClick={() => removeHighlight(index)}
                                            className="hover:text-blue-800"
                                        >
                                            <XCircleIcon className="h-4 w-4" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Page Type */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Page Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="page_type"
                                value={formData.page_type}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            >
                                <option value="home">Home Page</option>
                                <option value="home_construction_loan">Home Construction Loan</option>
                                <option value="home_purchase_loan">Home Purchase Loan</option>
                                <option value="home_composite_loan">Home Composite Loan</option>
                                <option value="home_renovation_loan">Home Renovation Loan</option>
                                <option value="home_extension_loan">Home Extension Loan</option>
                                <option value="balance_transfer_loan">Balance Transfer Loan</option>
                                <option value="nri_loan">NRI Loan</option>
                                <option value="top_up_loan">Top-up Loan</option>
                                <option value="mortgage_loan">Mortgage Loan</option>
                                <option value="about_aham">About Aham</option>
                                <option value="e_auction">E-Auction</option>
                                <option value="risk_classification">Risk Classification</option>
                                <option value="rating_certificate">Rating Certificate</option>
                                <option value="life_at_aham">Life at Aham</option>
                                <option value="gallery">Gallery</option>
                                <option value="investor_info">Investor Info</option>
                                <option value="pmay">PMAY</option>
                                <option value="governance">Governance</option>
                                <option value="dsa_onboarding">DSA Onboarding</option>
                                <option value="grievance_redressal">Grievance Redressal</option>
                                <option value="secured_assets">Secured Assets</option>
                                <option value="partner_us">Partner Us</option>
                                <option value="career">Career Page</option>
                                <option value="branch_locator">Branch Locator</option>
                                <option value="festival_campaign">Festival Campaign</option>

                            </select>
                        </div>

                        {/* Display Order & Active Status */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                    Display Order
                                </label>
                                <input
                                    type="number"
                                    name="display_order"
                                    value={formData.display_order}
                                    onChange={handleChange}
                                    min="0"
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                />
                            </div>

                            <div className="flex items-end">
                                <label className="flex items-center gap-3 cursor-pointer bg-slate-50 px-4 py-3 rounded-xl w-full hover:bg-slate-100 transition-colors">
                                    <input
                                        type="checkbox"
                                        name="is_active"
                                        checked={formData.is_active}
                                        onChange={handleChange}
                                        className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                                    />
                                    <span className="text-sm font-semibold text-slate-700">Active Banner</span>
                                </label>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-4 border-t border-slate-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Saving...' : banner ? 'Update Banner' : 'Create Banner'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Dimension Alert Dialog */}
            <AlertDialog
                isOpen={dimensionAlert.isOpen}
                onClose={() => setDimensionAlert({ isOpen: false, width: 0, height: 0, file: null })}
                onConfirm={() => {
                    const { file } = dimensionAlert;
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        setImageFile(file);
                        setImagePreview(reader.result);
                    };
                    reader.readAsDataURL(file);
                }}
                title="Image Dimension Warning"
                message={`Image dimensions are ${dimensionAlert.width}x${dimensionAlert.height}px.\nRecommended: 1600x800px.\n\nDo you want to continue anyway?`}
                confirmText="Continue Anyway"
            />
        </div>
    );
};

export default BannerModal;
